import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  Collapse,
  Fade,
} from '@mui/material';
import {
  SmartToy,
  Close,
  Send,
  Person,
  AutoAwesome,
} from '@mui/icons-material';
import { chatService, ChatMessage, SuggestedPrompt } from '../services/chatService';
import { useAuth } from '../contexts/AuthContext';

const ChatPopup: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedPrompt[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && suggestions.length === 0) {
      loadSuggestions();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSuggestions = async () => {
    try {
      const data = await chatService.getSuggestions();
      setSuggestions(data);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      session_id: sessionId || '',
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      console.log('[ChatPopup] Sending with sessionId:', sessionId);
      const response = await chatService.sendMessage(text, sessionId || undefined);
      console.log('[ChatPopup] Response session_id:', response.session_id);
      
      if (response.session_id && response.session_id !== sessionId) {
        console.log('[ChatPopup] Storing new sessionId:', response.session_id);
        setSessionId(response.session_id);
      }

      const assistantMessage: ChatMessage = {
        id: Date.now() + 1,
        session_id: response.session_id,
        role: 'assistant',
        content: response.response,
        created_at: new Date().toISOString(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          session_id: sessionId || '',
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      {/* Chat Window */}
      <Collapse in={isOpen} unmountOnExit>
        <Fade in={isOpen}>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 90,
              right: 24,
              width: { xs: 'calc(100vw - 48px)', sm: 380 },
              height: { xs: 'calc(100vh - 180px)', sm: 520 },
              maxHeight: 520,
              borderRadius: 3,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1300,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToy />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    AI Financial Assistant
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Your data stays private
                  </Typography>
                </Box>
              </Box>
              <Box>
                {messages.length > 0 && (
                  <IconButton 
                    size="small" 
                    onClick={startNewChat}
                    sx={{ color: 'white', mr: 0.5 }}
                    title="New chat"
                  >
                    <AutoAwesome fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                  <Close />
                </IconButton>
              </Box>
            </Box>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                bgcolor: '#f8fafc',
              }}
            >
              {messages.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    textAlign: 'center',
                  }}
                >
                  <AutoAwesome sx={{ fontSize: 48, color: 'primary.main', opacity: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Ask me anything about your finances
                  </Typography>
                  
                  {suggestions.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {suggestions.filter(s => s && s.text).slice(0, 4).map((suggestion, index) => (
                        <Chip
                          key={index}
                          label={suggestion.text}
                          size="small"
                          onClick={() => sendMessage(suggestion.text)}
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            '&:hover': { bgcolor: 'primary.light', color: 'white' },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <>
                  {messages.filter(m => m && m.role).map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start',
                        flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: message.role === 'user' ? 'primary.main' : 'grey.300',
                          color: message.role === 'user' ? 'white' : 'text.primary',
                          flexShrink: 0,
                        }}
                      >
                        {message.role === 'user' ? (
                          <Person sx={{ fontSize: 16 }} />
                        ) : (
                          <SmartToy sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          maxWidth: '80%',
                          bgcolor: message.role === 'user' ? 'primary.main' : 'white',
                          color: message.role === 'user' ? 'white' : 'text.primary',
                          borderRadius: 2,
                          border: message.role === 'assistant' ? '1px solid' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.875rem' }}
                        >
                          {message.content}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                  {loading && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.300',
                        }}
                      >
                        <SmartToy sx={{ fontSize: 16 }} />
                      </Box>
                      <Paper
                        elevation={0}
                        sx={{ p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                      >
                        <CircularProgress size={16} />
                      </Paper>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>

            {/* Input */}
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={3}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your finances..."
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => sendMessage()}
                        disabled={!input.trim() || loading}
                        color="primary"
                        size="small"
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, fontSize: '0.875rem' },
                }}
              />
            </Box>
          </Paper>
        </Fade>
      </Collapse>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        onClick={handleToggle}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          background: isOpen 
            ? 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)'
            : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          '&:hover': {
            background: isOpen
              ? 'linear-gradient(135deg, #db2777 0%, #ec4899 100%)'
              : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          },
        }}
      >
        {isOpen ? <Close /> : <SmartToy />}
      </Fab>
    </>
  );
};

export default ChatPopup;
